import type * as React from "react";
import { WIP_ACTION_HINT } from "@/components/ui/wip-hint";
import { cn } from "@/lib/utils";

/**
 * Shared layout shell for the treasury screens ported in design-port round 3
 * (ALL-5825): `/treasury` and `/treasury/staking`.
 *
 * These mirror the designer's own `features/treasury/treasury-ui.tsx` +
 * local `Panel` / `HomeMetricCard` helpers one-for-one — same classes, same
 * structure — and exist here because a second treasury page of the same round
 * renders the identical shells. They are deliberately the *only* thing shared:
 * the two pages' content is not.
 *
 * None of these helpers hold or fetch data. The treasury domain has no backend
 * in this environment, so a caller passes no value where the designer put one
 * and the page renders the shared unavailable marker or an empty state instead
 * (page-delivery-pipeline.md G2 `defer`). The point is to port the layout, not
 * the prototype's sample numbers.
 * Also hosts the disabled-control contract (`WIP_CONTROL_PROPS`, `WipReason`, `WipNotice`) shared by
 * `/treasury/staking/new`, `/treasury/lending` and `/treasury/liquidity`. Those three use `WipPanel`
 * (the designer card with a `className` slot); `/treasury` and `/treasury/staking` use `Panel`.
 */
export function MetricCard({
  value,
  label,
  sub,
}: {
  value: string;
  label: string;
  sub?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface px-5 py-[18px]">
      <div className="text-[28px] font-bold leading-[1.1] tracking-tight text-ink">{value}</div>
      <div className="mt-1 text-xs text-muted">{label}</div>
      {sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
    </div>
  );
}

/** Designer's `Panel`: a titled surface with an optional header action. */
export function Panel({
  title,
  bodyClassName,
  children,
  actions,
}: {
  title?: React.ReactNode;
  bodyClassName?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <section className="mb-5 overflow-hidden rounded-lg border border-line bg-surface">
      {title && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-5 py-3">
          <h3 className="text-[15px] font-bold text-ink">{title}</h3>
          {actions}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/**
 * A section heading with an optional right-hand action and an optional count
 * badge. The badge renders only for a real number: a count derived from data
 * this environment does not have is omitted, never shown as `0` or blank —
 * the same rule `TabsTrigger` in components/ui/tabs.tsx documents.
 */
export function SectionHeading({
  children,
  count,
  action,
}: {
  children: React.ReactNode;
  count?: number;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold text-ink">{children}</h2>
        {typeof count === "number" && Number.isFinite(count) && (
          <span className="rounded-full border border-info-border bg-info-soft px-2 py-0.5 text-[11px] font-bold text-info">
            {count}
          </span>
        )}
      </div>
      {action}
    </div>
  );
}

/**
 * The id every disabled control on these screens points at with
 * `aria-describedby`. One instance per screen, rendered by `WipReason`.
 */
export const WIP_REASON_ID = "treasury-wip-action-hint";

/**
 * The contract for a control whose action needs the missing backend: disabled,
 * with the reason as both a hover `title` and an announced description. Spread
 * onto an `Input`/`Select`/`Button`. Never pair this with an `onClick` — a
 * control that cannot act must not carry a handler that pretends it can.
 */
export const WIP_CONTROL_PROPS = {
  disabled: true,
  title: WIP_ACTION_HINT,
  "aria-describedby": WIP_REASON_ID,
} as const;

/** The single screen-reader-only node the disabled controls above describe. */
export function WipReason({ className }: { className?: string }) {
  return (
    <span id={WIP_REASON_ID} className={cn("sr-only", className)}>
      {WIP_ACTION_HINT}
    </span>
  );
}

/**
 * Honest statement of why the screen below shows no values. Announced as a
 * status region (a deferral is a settled state, not a failure), carrying the
 * screen's own copy — never a placeholder a reader could mistake for data.
 */
export function WipNotice({
  title,
  description,
  className,
}: {
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <section
      role="status"
      className={cn(
        "mb-5 flex flex-col gap-1 rounded-lg border border-dashed border-line bg-bg px-4 py-3",
        className,
      )}
    >
      <p className="text-sm font-semibold text-ink">{title}</p>
      <p className="text-xs leading-relaxed text-muted">{description}</p>
    </section>
  );
}

/**
 * Panel variant used by the staking-new, lending and liquidity screens (design-port round 3):
 * bordered card with an optional title row and action slot. */
export function WipPanel({
  title,
  actions,
  bodyClassName,
  className,
  children,
}: {
  title?: React.ReactNode;
  actions?: React.ReactNode;
  bodyClassName?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("rounded-lg border border-line bg-surface", className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
          {title && <div className="text-sm font-bold text-ink">{title}</div>}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={bodyClassName ?? "px-5 py-4"}>{children}</div>
    </div>
  );
}